import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HomeNavButton } from '../../components/NavButtons';
import { getStudentClasses } from '../../services/classService';
import { formatSubjectTitles } from '../../utils/classSubjectUtils';

function ArrowCircleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.63612 5.41078L6.69336 7.21477C6.38802 7.4983 6.38802 7.98157 6.69336 8.2651L8.63612 10.0691M7.70447 0.537475C3.74645 0.537475 0.537843 3.74608 0.537843 7.7041C0.537843 11.6621 3.74645 14.8707 7.70447 14.8707C11.6625 14.8707 14.8711 11.6621 14.8711 7.7041C14.8711 3.74608 11.6625 0.537475 7.70447 0.537475Z" stroke="white" strokeWidth="1.07499" strokeLinecap="round" />
    </svg>
  );
}

function ClassesPage() {
  const [classes, setClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const totalCapacity = useMemo(
    () => classes.reduce((sum, classItem) => sum + (Number(classItem.studentsMaxCounts) || 0), 0),
    [classes]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadClasses() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const classItems = await getStudentClasses();

        if (isMounted) {
          setClasses(classItems);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت کلاس‌ها وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadClasses();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p
            style={{ background: 'linear-gradient(90deg, #75E3F7 0%, #00C7EA 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
            className="px-6 py-1 rounded-[10px] text-[15px] text-white"
          >
            برنامه کلاسی
          </p>

          <img className="h-[50px]" src="/images/program.webp" alt="" />
        </div>

        <HomeNavButton />
      </div>

      <div>
        <p className="text-[11px]">کلاس‌های من</p>
        <p className="text-[11px] font-light">
          کلاس‌های خودت را ببین و برای مشاهده اطلاعات بیشتر وارد هر کلاس شو.
        </p>
      </div>

      <div
        className="grid grid-cols-2 gap-2 rounded-[8px] p-2"
        style={{ background: 'rgba(217, 217, 217, 0.3)' }}
      >
        <div className="rounded-[6px] px-3 py-2 text-white" style={{ background: 'rgba(0, 192, 185, 0.54)' }}>
          <p className="text-[11px] font-light">تعداد کلاس‌ها</p>
          <p className="mt-1 text-[17px] font-bold">{classes.length}</p>
        </div>

        <div className="rounded-[6px] px-3 py-2 text-white" style={{ background: '#FFB55A' }}>
          <p className="text-[11px] font-light">ظرفیت کل</p>
          <p className="mt-1 text-[17px] font-bold">{totalCapacity}</p>
        </div>
      </div>

      <div
        className="flex flex-col gap-2 rounded-[8px] p-2"
        style={{ background: 'rgba(217, 217, 217, 0.3)' }}
      >
        {isLoading ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center text-[12px] text-slate-500">
            در حال دریافت کلاس‌ها...
          </div>
        ) : errorMessage ? (
          <div className="rounded-[8px] bg-white px-4 py-5 text-center">
            <p className="text-[12px] font-bold text-[#E02727]">{errorMessage}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-3 rounded-[6px] bg-[#00C0B9] px-5 py-1 text-[12px] font-bold text-white"
            >
              تلاش دوباره
            </button>
          </div>
        ) : classes.length === 0 ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center">
            <p className="text-[13px] font-bold text-slate-800">فعلا کلاسی برای شما ثبت نشده است</p>
            <p className="mt-2 text-[11px] text-slate-500">بعد از اضافه شدن کلاس، اطلاعات آن اینجا نمایش داده می‌شود.</p>
          </div>
        ) : (
          classes.map((classItem, index) => {
            const subjectTitles = formatSubjectTitles(classItem);

            return (
              <Link
                key={classItem.id}
                to={`/classes/${classItem.id}`}
                className="flex flex-col gap-2 rounded-[8px] p-3 text-white"
                style={{ background: 'linear-gradient(90deg, #75E3F7 0%, #00C7EA 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] bg-white text-[12px] font-bold text-[#00A8A2]">
                      {index + 1}
                    </span>

                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-semibold">{classItem.title || 'کلاس بدون عنوان'}</p>
                      <p className="text-[10px] font-light text-white/90">
                        پایه {classItem.grade?.title || 'نامشخص'} | ظرفیت {classItem.studentsMaxCounts || 0} نفر
                      </p>
                      {subjectTitles && (
                        <p className="mt-0.5 truncate text-[10px] font-light text-white/90">
                          {subjectTitles}
                        </p>
                      )}
                    </div>
                  </div>

                  <ArrowCircleIcon />
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ClassesPage;
